import styled from '@emotion/styled';
import { Model, ParameterInput, Project } from '@arra/core';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import useProjects from '../../../project/context/useProjects';
import ProjectCard from '../../../project/ProjectCard';
import { sortByDate } from '../../../project/ProjectsUtils';
import {
  BlockContainer,
  BlockContent as BaseBlockContent,
  BlockHeader,
} from '../../ui';
import CreateProjectModal from './CreateProjectModal';
import Empty from './Empty';
import keyify from '../../../project/parameters/keyify';
import Button from '../../../ui/Button';
import { darken, Fab, Tooltip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { getServiceType } from '../../../analytics/posthogEvents';

const BlockContent = styled(BaseBlockContent)`
  flex: 1;
`;

export const NewProjectBlock = styled.div<{ color: string }>`
  background-color: ${({ color }) => darken(color, 0.2)};
  flex: none !important;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  height: 150px;
  width: 200px !important;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background-color: ${({ color }) => darken(color, 0.3)};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);

    svg {
      transform: scale(1.1);
    }
  }

  svg {
    width: 3rem;
    height: 3rem;
    color: black;
    transition: transform 0.2s ease;
  }
`;



interface ModelBlockProps {
  model: Model;
  projects: Project[] | undefined;
  count: number;
  remove: (id: string) => Promise<Partial<Project>>;
  duplicate: (params: { projectId: string }) => Promise<Partial<Project>>;
  sortOrder?: string;
  limit?: number;
  className?: string;
  fullHeight?: boolean;
}

const ModelBlock = ({
  model,
  projects,
  count,
  remove,
  duplicate,
  sortOrder,
  limit,
  className,
  fullHeight,
}: ModelBlockProps) => {
  const { t } = useTranslation(['dashboard', 'model']);
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [open, setOpen] = useState(false);
  const blockContentRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);

  useEffect(() => {
    if (blockContentRef.current) {
      const blockRect = blockContentRef.current.getBoundingClientRect();
      const lastCardRect = blockContentRef.current
        .querySelector(':scope > div:last-of-type')
        ?.getBoundingClientRect();
      if (!lastCardRect) {
        return;
      }
      setOverflow(
        blockRect.bottom < lastCardRect.bottom ||
        blockRect.right < lastCardRect.right,
      );
    }
  }, [blockContentRef, projects, projects?.length]);



  const sortedProjects = projects?.sort((a: Project, b: Project) => {
    if (sortOrder === 'date') {
      return sortByDate(a, b);
    }
    return a.completionRate - b.completionRate;
  }) || [];

  const handleSave = (values: ParameterInput[], modelType: string) => {
    navigate(`/project?values=${JSON.stringify(values)}&type=${modelType}`);
  };

  const handleDelete = (id: string) => async (event: any) => {
    return await remove(id);
  };

  const handleDuplicate = (id: string) => async (event: any) => {
    return await duplicate({ projectId: id });
  };

  const handleNewProjectClicked = () => {
    posthog?.capture('project_creation clicked', {
      step_name: 'project_creation_entry_clicked',
      step_order: 0,
      action: 'open_create_modal',
      button_name: 'new_project',
      page_name: 'dashboard',
      model_type: getServiceType(model.type),
    });
    setOpen(true);
  };
  const translationPrefix = `model:${keyify(model.name)}`;

  return (
    <>
      {open && <CreateProjectModal
        open={open}
        setOpen={setOpen}
        onSave={handleSave}
        model={model}
      />}
      <BlockContainer className={className}>
        <BlockHeader>
          <Link to={`/projects/${model.type}`}>
            {t(`${translationPrefix}.name`)} ({count})
          </Link>
          {overflow && (
            <Button
              startIcon={<ReadMoreIcon />}
              color="primary"
              variant="outlined"
              href={`/projects/${model.type}`}
              size="small"
            >
              {t('dashboard:models.block.see-more')}
            </Button>
          )}
        </BlockHeader>
        <BlockContent
          color={model.color}
          ref={blockContentRef}
          className={fullHeight ? 'full-height' : undefined}
        >
          {!projects || projects?.length === 0 && (
            <Empty model={model} onNewProject={handleNewProjectClicked} />
          )}
          {projects && projects.length > 0 && (
            <>
              <Tooltip title={t(`project:creation.${model.type}`)} arrow>
                <NewProjectBlock onClick={handleNewProjectClicked} color={model.color} >
                  <Add />
                </NewProjectBlock>
              </Tooltip>
              {sortedProjects?.map((project: Project) => (
                <ProjectCard
                  key={project._id.toString()}
                  project={project}
                  onClick={() => {
                    posthog?.capture('project opened', {
                      step_name: 'project_opened',
                      step_order: 1,
                      model_type: getServiceType(project.model.type),
                      from_page: 'dashboard',
                      to_page: 'project',
                    });
                    navigate(`/project/${project._id}`);
                  }}
                  onDelete={handleDelete(project._id.toString())}
                  onDuplicate={handleDuplicate(project._id.toString())}
                />
              ))}
            </>
          )}
        </BlockContent>
      </BlockContainer>
    </>
  );
};

export default ModelBlock;
